import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, Send, Mail, CalendarDays, HardDrive, AlertTriangle, ChevronDown, ChevronUp, Settings2, Loader2, PenLine, Check } from 'lucide-react';
import { BriefRecipients } from './BriefRecipients';
import { MailComposer } from './mail/MailComposer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/daily-brief`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '');

const md = {
  h2: ({ children }) => <h2 className="text-lg md:text-xl font-black text-slate-900 mt-1 mb-2 tracking-tight">{children}</h2>,
  h3: ({ children }) => <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-sky-800 mt-5 mb-2 flex items-center gap-2 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-sm before:bg-sky-600">{children}</h3>,
  p: ({ children }) => <p className="text-sm text-slate-700 leading-relaxed mb-2">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 mb-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 mb-2">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-slate-700 leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="text-slate-900 font-bold">{children}</strong>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noreferrer" className="text-sky-700 underline decoration-sky-300 hover:text-sky-900">{children}</a>,
};

const GMAIL_THREAD = /mail\.google\.com\/mail\/u\/\d+\/#(?:all|inbox|sent|search\/[^/]+)\/([0-9a-f]{10,})/i;
const mdChat = { ...md, p: ({ children }) => <p className="text-sm text-slate-700 leading-relaxed mb-1">{children}</p> };

const textOf = (n) => (typeof n === 'string' || typeof n === 'number') ? String(n) : Array.isArray(n) ? n.map(textOf).join('') : n?.props?.children ? textOf(n.props.children) : '';
const hrefsOf = (n, acc = []) => { if (Array.isArray(n)) n.forEach(x => hrefsOf(x, acc)); else if (n?.props) { if (n.props.href) acc.push(n.props.href); hrefsOf(n.props.children, acc); } return acc; };
const keyOf = (text) => { let h = 5381; const s = text.toLowerCase().replace(/\s+/g, ' ').trim(); for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0; return `k${(h >>> 0).toString(36)}`; };
const startsWithTimeBlock = (node, children) => node?.children?.[0]?.tagName === 'strong' && /^\s*\d{1,2}:\d{2}/.test(textOf(children));

const BriefItem = ({ as: Tag = 'li', children, done, doneThreads, onToggle, className = '' }) => {
  const text = textOf(children);
  const key = keyOf(text);
  const tids = hrefsOf(children).map(h => h.match(GMAIL_THREAD)?.[1]).filter(Boolean);
  const viaThread = tids.some(t => doneThreads.has(t));
  const isDone = Boolean(done[key]) || viaThread;
  useEffect(() => { if (viaThread && !done[key]) onToggle(key, true, text, true); }, [viaThread]); // eslint-disable-line
  return (
    <Tag className={`flex items-start gap-2 group ${className}`} data-testid={`brief-item-${key}`}>
      <button onClick={() => onToggle(key, !isDone, text)} title={isDone ? 'Done — click to undo' : 'Mark done'} data-testid={`brief-item-check-${key}`}
        className={`mt-[3px] w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-all ${isDone ? 'bg-emerald-500 border-emerald-500 text-white shadow-[0_0_0_3px_rgba(16,185,129,0.18)]' : 'border-slate-300 bg-white hover:border-emerald-500 hover:bg-emerald-50 group-hover:border-emerald-400'}`}>
        {isDone && <Check className="w-3 h-3" strokeWidth={3} />}
      </button>
      <span className={`flex-1 min-w-0 transition-colors ${isDone ? 'line-through text-slate-500 [&_strong]:text-slate-500 [&_a]:text-slate-500' : ''}`}>{children}</span>
      {isDone && <span className="text-[9px] font-black uppercase tracking-wider text-emerald-600 mt-1 shrink-0" data-testid={`brief-item-done-${key}`}>done</span>}
    </Tag>
  );
};

const countItems = (markdown = '') => (markdown.match(/^\s*[-*] /gm) || []).length + (markdown.match(/^\*\*\d{1,2}:\d{2}/gm) || []).length;

// Checklist state travels by context so the markdown component map stays referentially stable —
// a fresh map each render makes ReactMarkdown remount every node and the scroll box snaps back to the top on each tick.
const BriefCtx = createContext({ done: {}, doneThreads: new Set(), onToggle: () => {}, briefId: null, onDraft: () => {}, busyId: '' });
const CtxItem = ({ as, className, children }) => {
  const { done, doneThreads, onToggle } = useContext(BriefCtx);
  return <BriefItem as={as} done={done} doneThreads={doneThreads} onToggle={onToggle} className={className}>{children}</BriefItem>;
};

const DraftReplyLink = ({ href, children, briefId, onDraft, busyId }) => {
  const tid = (href || '').match(GMAIL_THREAD)?.[1];
  const link = <a href={href} target="_blank" rel="noreferrer" className="text-sky-700 underline decoration-sky-300 hover:text-sky-900">{children}</a>;
  if (!tid) return link;
  const busy = busyId === tid;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {link}
      <button onClick={() => onDraft(tid)} disabled={Boolean(busyId)} title="Draft a reply from this item and the thread — you review before sending"
        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-sky-50 border border-sky-200 text-sky-800 hover:bg-sky-100 disabled:opacity-50" data-testid={`brief-draft-reply-${tid}`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <PenLine className="w-3 h-3" />}{busy ? 'Drafting…' : 'Draft reply'}
      </button>
    </span>
  );
};
const CtxDraftLink = (p) => {
  const { briefId, onDraft, busyId } = useContext(BriefCtx);
  return <DraftReplyLink {...p} briefId={briefId} onDraft={onDraft} busyId={busyId} />;
};
const mdChecklist = {
  ...md,
  a: CtxDraftLink,
  ul: ({ children }) => <ul className="pl-0 space-y-1 mb-2 list-none">{children}</ul>,
  ol: ({ children }) => <ol className="pl-0 space-y-1 mb-2 list-none">{children}</ol>,
  li: ({ children }) => <CtxItem className="text-sm text-slate-700 leading-relaxed">{children}</CtxItem>,
  p: ({ node, children }) => startsWithTimeBlock(node, children)
    ? <CtxItem as="p" className="text-sm text-slate-800 leading-relaxed mt-3 mb-1">{children}</CtxItem>
    : <p className="text-sm text-slate-700 leading-relaxed mb-2">{children}</p>,
};

export default function DailyBriefCard() {
  const [data, setData] = useState(null);
  const [gstatus, setGstatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const [q, setQ] = useState('');
  const [chat, setChat] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [draft, setDraft] = useState(null);
  const [draftBusy, setDraftBusy] = useState('');
  const endRef = useRef(null);

  const draftReply = async (tid) => {
    setDraftBusy(tid);
    try {
      const r = await axios.post(`${API}/draft-reply`, { thread_id: tid, brief_id: data?.brief?.id }, { ...auth(), timeout: 120000 });
      setDraft({ ...r.data, ai: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not draft a reply'); }
    setDraftBusy('');
  };

  const doneItems = data?.brief?.done_items || {};
  const doneThreads = useMemo(() => new Set(data?.brief?.done_threads || []), [data?.brief?.done_threads]);
  const toggleDone = async (key, done, text, silent = false) => {
    const briefId = data?.brief?.id;
    if (!briefId) return;
    setData(d => { const items = { ...(d.brief.done_items || {}) }; if (done) items[key] = { text, at: new Date().toISOString() }; else delete items[key]; return { ...d, brief: { ...d.brief, done_items: items } }; });
    try {
      const r = await axios.post(`${API}/${briefId}/done`, { key, done, text }, auth());
      setData(d => ({ ...d, brief: { ...d.brief, done_items: r.data.done_items, done_threads: r.data.done_threads } }));
    } catch { if (!silent) toast.error('Could not save that tick'); }
  };
  const briefCtx = { done: doneItems, doneThreads, onToggle: toggleDone, briefId: data?.brief?.id, onDraft: draftReply, busyId: draftBusy };
  const totalItems = countItems(data?.brief?.markdown);
  const doneCount = Math.min(Object.keys(doneItems).length, totalItems);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/today`, auth());
      setData(r.data);
      if (r.data.brief?.id) {
        const h = await axios.get(`${API}/chat/${r.data.brief.id}`, auth());
        setChat(h.data.messages || []);
      }
    } catch { /* not for this role */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.settings?.enabled) axios.get(`${API}/google-status`, auth()).then(r => setGstatus(r.data)).catch(() => {});
  }, [data?.settings?.enabled]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/generate`, {}, { ...auth(), timeout: 300000 });
      setData(d => ({ ...d, brief: r.data, is_today: true }));
      setChat([]);
      toast.success('Brief written');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not write the brief'); }
    setBusy(false);
  };

  const toggleEnabled = async () => {
    try {
      const r = await axios.put(`${API}/settings`, { enabled: !data.settings.enabled }, auth());
      setData(d => ({ ...d, settings: { ...d.settings, ...r.data } }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  const setHour = async (hour) => {
    try {
      const r = await axios.put(`${API}/settings`, { hour }, auth());
      setData(d => ({ ...d, settings: { ...d.settings, ...r.data } }));
      toast.success(`Brief lands at ${hour}:00 each morning`);
    } catch (e) { toast.error('Could not save'); }
  };

  const ask = async (e) => {
    e?.preventDefault();
    const question = q.trim();
    if (!question || !data?.brief?.id || streaming) return;
    setQ('');
    setChat(c => [...c, { role: 'user', content: question }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat/${data.brief.id}`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...auth().headers }, body: JSON.stringify({ question }) });
      if (!res.ok || !res.body) throw new Error((await res.json().catch(() => ({}))).detail || 'Request failed');
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const frames = buf.split('\n\n');
        buf = frames.pop();
        for (const f of frames) {
          if (f.startsWith('event: done')) continue;
          const line = f.split('\n').find(l => l.startsWith('data: '));
          if (line === undefined) continue;
          const chunk = line.slice(6).replace(/\\n/g, '\n');
          setChat(c => { const n = [...c]; n[n.length - 1] = { ...n[n.length - 1], content: n[n.length - 1].content + chunk }; return n; });
        }
      }
    } catch (err) {
      setChat(c => { const n = [...c]; n[n.length - 1] = { role: 'assistant', content: `_${err.message}_` }; return n; });
    }
    setStreaming(false);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  if (!data || !data.settings?.can_use) return null;
  const { settings, brief } = data;

  if (!settings.enabled) {
    return (
      <div className="rounded-2xl ti-panel px-5 py-4 flex items-center gap-3 flex-wrap" data-testid="daily-brief-off">
        <Sparkles className="w-5 h-5 text-sky-700" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900">Daily brief is off for you</p>
          <p className="text-xs text-slate-500">Every morning: your inbox, calendar and what's happening in ops, turned into the day's priorities.</p>
        </div>
        <button onClick={toggleEnabled} data-testid="daily-brief-enable" className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-sky-800 text-white text-sm font-bold shadow-sm">Turn it on</button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl ti-panel overflow-hidden" data-testid="daily-brief-card">
      <div className="px-4 sm:px-5 pt-4 pb-3 flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-600 to-blue-900 shadow-[0_6px_16px_rgba(29,78,216,0.28)] flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-white" /></div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base md:text-lg font-black text-slate-900 tracking-tight">Your day</h2>
            {brief && <span className="text-[11px] text-slate-600" data-testid="daily-brief-when">written {fmtTime(brief.generated_at)}{brief.trigger === 'scheduled' ? ' · scheduled' : ''}</span>}
            {brief && !data.is_today && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">from {brief.date}</span>}
            {brief && !brief.error && totalItems > 0 && (
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-0.5 rounded-full border ${doneCount === totalItems ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-emerald-200 text-emerald-700'}`} data-testid="daily-brief-progress" title="Tick items off as you finish them — replies you send from ops tick themselves">
                <Check className="w-3 h-3" strokeWidth={3} />{doneCount}/{totalItems} done
                <span className="w-16 h-1.5 rounded-full bg-emerald-100 overflow-hidden"><span className="block h-full bg-emerald-500 transition-all" style={{ width: `${Math.round((doneCount / totalItems) * 100)}%` }} /></span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-slate-600 mt-0.5 flex-wrap">
            {brief?.google_ok ? (
              <span className="inline-flex items-center gap-1 flex-wrap" data-testid="daily-brief-sources"><Mail className="w-3 h-3" />{brief.sources?.inbox} inbox · {brief.sources?.sent} sent · {brief.sources?.drafts} drafts <CalendarDays className="w-3 h-3 ml-1" />{brief.sources?.events} events
                {brief.drive_error ? (
                  <span className="inline-flex items-center gap-1 text-amber-600 ml-1" title={brief.drive_error.action} data-testid="daily-brief-drive-warning"><HardDrive className="w-3 h-3" /> Drive not shared yet</span>
                ) : (
                  <span className="inline-flex items-center gap-1 ml-1" data-testid="daily-brief-drive-count"><HardDrive className="w-3 h-3" />{brief.sources?.drive ?? 0} Drive files</span>
                )}
              </span>
            ) : gstatus && !gstatus.ok ? (
              <span className="inline-flex items-center gap-1 text-amber-600" title={gstatus.action} data-testid="daily-brief-google-warning"><AlertTriangle className="w-3 h-3" /> Google access not authorized yet — ops data only</span>
            ) : null}
            <span>lands {settings.hour}:00 AM · {data.model}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          <button onClick={() => setShowSettings(s => !s)} className="p-2 rounded-lg hover:bg-slate-900/5 text-slate-500 hover:text-slate-900" title="Brief settings" data-testid="daily-brief-settings"><Settings2 className="w-4 h-4" /></button>
          <button onClick={generate} disabled={busy} data-testid="daily-brief-generate"
            className="px-3 py-2 rounded-xl bg-slate-900 hover:bg-sky-800 disabled:bg-slate-300 text-white disabled:text-slate-500 text-sm font-bold flex items-center gap-1.5 shadow-sm transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}{busy ? 'Reading your inbox…' : brief ? 'Rewrite now' : 'Write my brief'}
          </button>
          <button onClick={() => setOpen(o => !o)} className="p-2 rounded-lg hover:bg-slate-900/5 text-slate-500 hover:text-slate-900" data-testid="daily-brief-toggle">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
        </div>
      </div>

      {showSettings && (
        <div className="mx-5 mb-3 rounded-xl border border-slate-200/80 bg-slate-50/80 px-4 py-3 flex items-center gap-4 flex-wrap text-sm" data-testid="daily-brief-settings-panel">
          <label className="text-slate-700 flex items-center gap-2">Delivered at
            <select value={settings.hour} onChange={e => setHour(parseInt(e.target.value))} className="bg-white text-slate-900 rounded-lg px-2 py-1 border border-slate-300" data-testid="daily-brief-hour">
              {[5, 6, 7, 8, 9, 10, 11].map(h => <option key={h} value={h}>{h}:00 AM</option>)}
            </select>
          </label>
          {gstatus && !gstatus.ok && <span className="text-xs text-amber-700 flex-1 min-w-[240px]"><b>Google:</b> {gstatus.message} {gstatus.action}</span>}
          {gstatus?.ok && <span className="text-xs text-emerald-700">Google connected · {gstatus.email}{gstatus.drive_ok ? ' · Drive connected' : ''}</span>}
          {gstatus?.ok && gstatus.drive_ok === false && <span className="text-xs text-amber-700 flex-1 min-w-[240px]" data-testid="daily-brief-drive-action"><b>Drive:</b> {gstatus.drive_error}. {gstatus.drive_action}</span>}
          <button onClick={toggleEnabled} className="ml-auto text-xs text-slate-500 hover:text-slate-900 underline" data-testid="daily-brief-disable">Turn the brief off</button>
          <BriefRecipients />
        </div>
      )}

      {open && (
        <div className="px-4 sm:px-5 pb-4">
          {!brief ? (
            <p className="text-sm text-slate-600 py-6 text-center">No brief yet — hit <b>Write my brief</b>, or wait for {settings.hour}:00 tomorrow.</p>
          ) : brief.error ? (
            <p className="text-sm text-red-600 py-4" data-testid="daily-brief-error">{brief.error}</p>
          ) : (
            <div className="border-t border-slate-200/60 pt-4 px-1 max-h-[520px] overflow-y-auto" data-testid="daily-brief-body">
              <BriefCtx.Provider value={briefCtx}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdChecklist}>{brief.markdown}</ReactMarkdown>
              </BriefCtx.Provider>
            </div>
          )}

          {brief && !brief.error && (
            <div className="mt-3">
              {chat.length > 0 && (
                <div className="space-y-2 mb-2 max-h-72 overflow-y-auto pr-1" data-testid="daily-brief-chat">
                  {chat.map((m, i) => (
                    <div key={i} className={`text-sm rounded-xl px-3 py-2 ${m.role === 'user' ? 'bg-sky-50 text-sky-950 ml-8 border border-sky-100' : 'bg-white text-slate-700 mr-4 border border-slate-200/70'}`}>
                      {m.role === 'user' ? m.content : <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdChat}>{m.content || (streaming ? '…' : '')}</ReactMarkdown>}
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
              )}
              <form onSubmit={ask} className="flex items-center gap-2">
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Ask about today — “what's waiting on Blair?”, “draft a reply to Axiom”…"
                  className="flex-1 px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-900 text-sm placeholder:text-slate-400 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100" data-testid="daily-brief-ask-input" />
                <button type="submit" disabled={streaming || !q.trim()} className="p-2.5 rounded-xl bg-slate-900 hover:bg-sky-800 disabled:opacity-40 text-white transition-colors" data-testid="daily-brief-ask-send">
                  {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </div>
          )}
        </div>
      )}
      {draft && <MailComposer draft={draft} onClose={() => setDraft(null)} onSent={() => setTimeout(load, 800)} />}
    </div>
  );
}
